import { FE_LOG_CLIENT_ERROR_API } from '@/helper/apiConstants'
import { isNoisyClientError } from '@/utils/clientErrorNoise'
import { FIELD_LIMITS, clip } from '@/utils/clientErrorLimits'

interface ClientErrorPayload {
  message: string
  stack?: string
  componentStack?: string
  digest?: string
  errorName?: string
  boundary?: string
  source:
    | 'error-boundary'
    | 'section-error-boundary'
    | 'global-error-boundary'
    | 'window-error'
    | 'unhandled-rejection'
}

const reported = new Set<string>()
const MAX_REPORTS_PER_PAGE_LOAD = 5

export function reportClientError(payload: ClientErrorPayload) {
  try {
    if (typeof window === 'undefined') return
    // Suppressing here also avoids the POST, which would itself be billed as a Cloud Run
    // request log. The route re-checks, so a stale bundle cannot bypass the filter.
    if (isNoisyClientError(payload.message)) return

    // boundary is part of the key: the same message from global-error and from a
    // component boundary are distinct events worth reporting separately.
    const key = `${payload.source}:${payload.boundary ?? ''}:${payload.message}`
    if (reported.has(key) || reported.size >= MAX_REPORTS_PER_PAGE_LOAD) return
    reported.add(key)

    void fetch(FE_LOG_CLIENT_ERROR_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Clipped here rather than at each call site: the API applies the same
      // limits anyway, so anything longer is weight the beacon never needs
      body: JSON.stringify({
        message: clip(payload.message, FIELD_LIMITS.message),
        stack: clip(payload.stack, FIELD_LIMITS.stack),
        componentStack: clip(
          payload.componentStack,
          FIELD_LIMITS.componentStack
        ),
        digest: clip(payload.digest, FIELD_LIMITS.digest),
        errorName: clip(payload.errorName, FIELD_LIMITS.errorName),
        boundary: clip(payload.boundary, FIELD_LIMITS.boundary),
        // A union of short literals — nothing to clip
        source: payload.source,
        pathname: clip(window.location.pathname, FIELD_LIMITS.pathname),
      }),
      // Let the request finish even if the user navigates away or the tab closes
      keepalive: true,
    }).catch(() => {})
  } catch {
    // Reporting must never throw — it runs inside error-handling paths
  }
}
